import { CheckCircle2, KeyRound } from "lucide-react";
import { mxn, fdate } from "@/lib/format";
import { TITULO_RENTAS, LEYENDA_RENTAS_CUBIERTAS, separarResidual, rentasCubiertas } from "@/lib/arrendamiento";
import { TablaAmortizacionCredito, moraDeRenglon } from "@/components/creditos/TablaAmortizacionCredito";

const COLOR_ESTATUS = { Pagada: "text-emerald-600", Vencida: "text-red-600", Parcial: "text-amber-600" };

/** Leyenda derivada: todas las rentas pagadas y el residual aún dentro del plazo de opción terminal. */
export function LeyendaRentasCubiertas({ tabla, dataTestid = "arr-leyenda-rentas-cubiertas" }) {
  if (!rentasCubiertas(tabla)) return null;
  return (
    <span data-testid={dataTestid}
      className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10.5px] font-semibold text-sky-800">
      <KeyRound className="h-3 w-3" /> {LEYENDA_RENTAS_CUBIERTAS}
    </span>
  );
}

/** Pie de la tabla: el residual NO se lista como renta; se muestra aquí con su fecha límite. */
export function PieResidualArrendamiento({ tabla, dataTestid = "arr-pie-residual", className = "border-t border-slate-200" }) {
  const { residual } = separarResidual(tabla);
  if (!residual) return null;
  const mora = residual.estatus === "Vencida" ? moraDeRenglon(residual) : null;
  const iva = Number(residual.iva_interes || 0);
  return (
    <div data-testid={dataTestid}
      className={`flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-3 py-2 text-xs text-slate-700 ${className}`}>
      <p>
        Valor residual al término:{" "}
        <b className="font-mono tabular-nums text-slate-900" data-testid={`${dataTestid}-monto`}>{mxn(residual.capital)}</b>
        {iva > 0 && <span className="text-slate-500"> + IVA {mxn(iva)}</span>}
        {" · "}fecha límite de opción terminal:{" "}
        <b className="text-slate-900" data-testid={`${dataTestid}-fecha`}>{fdate(residual.fecha_pago)}</b>
      </p>
      {residual.estatus === "Pagada" && (
        <span className="inline-flex items-center gap-1 text-emerald-600" data-testid={`${dataTestid}-pagado`}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Residual pagado
        </span>
      )}
      {residual.estatus === "Vencida" && (
        <span className="font-semibold text-red-600" data-testid={`${dataTestid}-vencido`}>
          Vencido sin opción ejercida{mora ? ` · mora ${mxn(mora.monto)} (${mora.dias || 0} días)` : ""}
        </span>
      )}
    </div>
  );
}

function Encabezado({ tabla, dataTestid }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
      <p className="text-sm font-semibold text-slate-800" data-testid={`${dataTestid}-titulo`}>{TITULO_RENTAS}</p>
      <LeyendaRentasCubiertas tabla={tabla} dataTestid={`${dataTestid}-leyenda`} />
    </div>
  );
}

/** Tabla de rentas (proyección, definitiva y simulador). Columnas: renta, IVA y total. */
export function TablaArrendamiento({ tabla, dataTestid = "tabla-arrendamiento", conEstatus = true }) {
  const { rentas } = separarResidual(tabla);
  if (rentas.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500"
        data-testid={`${dataTestid}-empty`}>
        Sin tabla de rentas.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white" data-testid={dataTestid}>
      <Encabezado tabla={tabla} dataTestid={dataTestid} />
      {/* w-0 min-w-full: no ensancha diálogos en grid (Mesa de Control); el scroll horizontal queda aquí. */}
      <div className="max-h-[520px] w-0 min-w-full overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-slate-50">
            <tr className="text-xs uppercase tracking-wide text-slate-500">
              <th className="px-2 py-2">#</th>
              <th className="px-2 py-2">Fecha</th>
              <th className="px-2 py-2 text-right">Capital</th>
              <th className="px-2 py-2 text-right">Interés</th>
              <th className="px-2 py-2 text-right" title="Capital + interés — renta fija">Renta</th>
              <th className="px-2 py-2 text-right">IVA</th>
              <th className="px-2 py-2 text-right" title="Renta + IVA del periodo">Total</th>
              <th className="px-2 py-2 text-right">Saldo</th>
              {conEstatus && <th className="px-2 py-2 text-right">Estatus</th>}
            </tr>
          </thead>
          <tbody>
            {rentas.map((c) => (
              <tr key={c.no} className="border-t border-slate-100 tabular-nums" data-testid={`${dataTestid}-fila-${c.no}`}>
                <td className="px-2 py-2 text-slate-500">{c.no}</td>
                <td className="whitespace-nowrap px-2 py-2 text-slate-500">{fdate(c.fecha_pago)}</td>
                <td className="px-2 py-2 text-right font-mono">{mxn(c.capital)}</td>
                <td className="px-2 py-2 text-right font-mono">{mxn(c.interes)}</td>
                <td className="px-2 py-2 text-right font-mono font-semibold">{mxn(c.cuota_credito)}</td>
                <td className="px-2 py-2 text-right font-mono">{mxn(c.iva_interes)}</td>
                <td className="px-2 py-2 text-right font-mono">{mxn(c.total_periodo ?? c.pago)}</td>
                <td className="px-2 py-2 text-right font-mono text-slate-500">{mxn(c.saldo_insoluto)}</td>
                {conEstatus && (
                  <td className="px-2 py-2 text-right text-xs">
                    <span className={COLOR_ESTATUS[c.estatus] || "text-slate-500"}>{c.estatus || "Pendiente"}</span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PieResidualArrendamiento tabla={tabla} dataTestid={`${dataTestid}-pie-residual`} />
    </div>
  );
}

/** Cartera y Pagos: la tabla operativa (mora, pagado, total exigible) solo con las rentas + pie del residual. */
export function TablaRentasCredito({ tabla, testId = "tabla-rentas-credito", ...props }) {
  const { rentas } = separarResidual(tabla);
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <TablaAmortizacionCredito tabla={rentas} testId={`${testId}-grid`} {...props} />
      <PieResidualArrendamiento tabla={tabla} dataTestid={`${testId}-pie-residual`}
        className="rounded-lg border border-slate-200" />
    </div>
  );
}
